import {CalendarOutlined, DollarOutlined, InfoCircleOutlined, UnorderedListOutlined} from "@ant-design/icons";
import {Alert, Breadcrumb, Button, Card, Col, DatePicker, Divider, Empty, Form, Layout, Row, Skeleton} from "antd";
import ptBR from "antd/locale/pt_BR";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {apiExpectedReceivablesByDate, apiExpectedReceivablesByMonth, apiExpectedReceivablesNext, apiExpectedReceivablesTotal} from "../../../../api/receivables";
import {formatDate, formatPrice} from "../../../../utils/format";
import AccountsSearchSelect from "../../../common/AccountsSearchSelect/AccountsSearchSelect";
import {Bar, BarChart, CartesianGrid, Customized, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import "./ReceivableGraphic.scss";

dayjs.locale("pt-br");

const {Content} = Layout;
const {RangePicker} = DatePicker;

const ReceivableGraphic = () => {
  const navigate = useNavigate();
  const [total, setTotal] = useState(null);
  const [nextPayment, setNextPayment] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [rangeTouched, setRangeTouched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs(),
    dayjs().add(1, "month").endOf("month"),
  ]);

  const months = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return [];
    const start = dateRange[0].startOf("month");
    const end = dateRange[1].endOf("month");
    const result = [];
    let current = start;
    while (current.isBefore(end) || current.isSame(end, "month")) {
      result.push(current);
      current = current.add(1, "month");
    }
    return result;
  }, [dateRange]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const totalRes = await apiExpectedReceivablesTotal(selectedAccount?.id);
        setTotal(totalRes.total);
      } catch (error) {
        console.error("Error fetching receivables total:", error);
      }

      try {
        const nextRes = await apiExpectedReceivablesNext(selectedAccount?.id);
        setNextPayment(nextRes);
      } catch (error) {
        console.error("Error fetching next receivable:", error);
      }

      setLoading(false);
    };

    fetchData();
  }, [selectedAccount]);

  useEffect(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    const fetchDaily = async () => {
      try {
        const dailyRes = await apiExpectedReceivablesByDate({
          date_after: dateRange[0].startOf("month").format("YYYY-MM-DD"),
          date_before: dateRange[1].endOf("month").format("YYYY-MM-DD"),
        }, selectedAccount?.id);
        setDailyData(Array.isArray(dailyRes) ? dailyRes : []);
      } catch (error) {
        console.error("Error fetching receivables by date:", error);
      }
    };

    fetchDaily();
  }, [dateRange, selectedAccount]);

  const dailyMap = useMemo(() => {
    const map = {};
    dailyData.forEach((item) => {
      map[item.date] = item.total;
    });
    return map;
  }, [dailyData]);

  const monthlyRange = useMemo(() => {
    if (rangeTouched && dateRange && dateRange[0] && dateRange[1]) {
      return {
        date_after: dateRange[0].startOf("month").format("YYYY-MM-DD"),
        date_before: dateRange[1].endOf("month").format("YYYY-MM-DD"),
      };
    }
    return {
      date_after: dayjs().subtract(6, "month").startOf("month").format("YYYY-MM-DD"),
      date_before: dayjs().add(3, "month").endOf("month").format("YYYY-MM-DD"),
    };
  }, [rangeTouched, dateRange]);

  useEffect(() => {
    const fetchMonthly = async () => {
      try {
        const res = await apiExpectedReceivablesByMonth(monthlyRange, selectedAccount?.id);
        setMonthlyData(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Error fetching receivables by month:", error);
        setMonthlyData([]);
      }
    };

    fetchMonthly();
  }, [monthlyRange, selectedAccount]);

  const monthlyChartData = useMemo(() => {
    const totals = {};
    monthlyData.forEach((item) => {
      totals[dayjs(item.month).format("YYYY-MM")] = item.total / 100;
    });
    const start = dayjs(monthlyRange.date_after).startOf("month");
    const end = dayjs(monthlyRange.date_before).startOf("month");
    const result = [];
    let current = start;
    while (current.isBefore(end) || current.isSame(end, "month")) {
      const key = current.format("YYYY-MM");
      result.push({
        key,
        label: current.locale("pt-br").format("MMM/YY"),
        value: totals[key] || 0,
      });
      current = current.add(1, "month");
    }
    return result;
  }, [monthlyData, monthlyRange]);

  const hasAnyMonthlyValue = useMemo(
    () => monthlyChartData.some((item) => item.value !== 0),
    [monthlyChartData]
  );

  const renderTodayLine = (props) => {
    const {xAxisMap, offset} = props;
    if (!xAxisMap || !offset) return null;
    const axis = Object.values(xAxisMap)[0];
    if (!axis || typeof axis.scale !== "function") return null;
    const currentKey = dayjs().format("YYYY-MM");
    const point = monthlyChartData.find((item) => item.key === currentKey);
    if (!point) return null;
    const bandStart = axis.scale(point.label);
    if (bandStart == null || Number.isNaN(bandStart)) return null;
    const bandwidth = typeof axis.scale.bandwidth === "function" ? axis.scale.bandwidth() : 0;
    const today = dayjs();
    const fraction = (today.date() - 0.5) / today.daysInMonth();
    const x = bandStart + bandwidth * fraction;
    return (
      <line
        x1={x}
        y1={offset.top}
        x2={x}
        y2={offset.top + offset.height}
        stroke="#ff4d4f"
        strokeWidth={2}
        strokeDasharray="4 3"
        pointerEvents="none"
      />
    );
  };


  const renderMonth = (monthDate) => {
    const startOfMonth = monthDate.startOf("month");
    const daysInMonth = monthDate.daysInMonth();
    const startDay = startOfMonth.day();

    const weeks = [];
    let currentDay = 1;
    let week = new Array(7).fill(null);

    for (let i = startDay; i < 7 && currentDay <= daysInMonth; i++) {
      week[i] = currentDay++;
    }
    weeks.push(week);

    while (currentDay <= daysInMonth) {
      week = new Array(7).fill(null);
      for (let i = 0; i < 7 && currentDay <= daysInMonth; i++) {
        week[i] = currentDay++;
      }
      weeks.push(week);
    }

    const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

    return (
      <div className="calendar-month">
        <h4 className="calendar-month-title">
          {monthDate.locale("pt-br").format("MMMM YYYY").charAt(0).toUpperCase() + monthDate.locale("pt-br").format("MMMM YYYY").slice(1)}
        </h4>
        <div className="calendar-grid">
          {weekDays.map((day) => (
            <div key={day} className="calendar-header-cell">
              {day}
            </div>
          ))}
          {weeks.map((week, wIdx) =>
            week.map((day, dIdx) => {
              if (!day) {
                return <div key={`${wIdx}-${dIdx}`} className="calendar-cell empty" />;
              }

              const dateStr = monthDate.date(day).format("YYYY-MM-DD");
              const amount = dailyMap[dateStr];
              const hasAmount = amount !== undefined && amount !== 0;
              const isToday = dayjs().format("YYYY-MM-DD") === dateStr;
              const isWeekend = dIdx === 0 || dIdx === 6;

              return (
                <div
                  key={`${wIdx}-${dIdx}`}
                  className={`calendar-cell clickable ${hasAmount ? "has-value" : ""} ${isToday ? "today" : ""} ${isWeekend ? "weekend" : ""}`}
                  onClick={() => navigate(`/listagem-recebiveis/registros?from=${dateStr}&to=${dateStr}`)}
                >
                  <span className="calendar-day">{day}</span>
                  {hasAmount && (
                    <span className={`calendar-amount ${amount < 0 ? "negative" : ""}`}>
                      {formatPrice(amount / 100)}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };


  return (
    <div className="ReceivableGraphic">
      <Layout style={{padding: "1.25rem"}}>
        <Breadcrumb style={{margin: "16px 0"}}>
          <div style={{display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center"}}>
            <h2 className="title-page">Agenda de recebimento</h2>
            <Button
              type="primary"
              icon={<UnorderedListOutlined />}
              onClick={() => navigate("/listagem-recebiveis/registros")}
            >
              Ver registros
            </Button>
          </div>
          <Divider />
          <Breadcrumb.Item>Minhas vendas</Breadcrumb.Item>
          <Breadcrumb.Item>Agenda de recebimento</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: "transparent",
          }}
        >
          {loading ? (
            <Skeleton active />
          ) : (
            <>
              <Card title="Visão geral" className="overview-card" style={{marginBottom: 16}}>
                <div className="filters-bar">
                  <Form.Item label="Conta" style={{margin: 0}}>
                    <AccountsSearchSelect
                      value={selectedAccount}
                      onChange={setSelectedAccount}
                      placeholder="Minha Conta"
                      allowClear
                      style={{width: 250}}
                    />
                  </Form.Item>
                  <Form.Item label="Período" style={{margin: 0}}>
                    <RangePicker
                      onChange={(dates) => {
                        setDateRange(dates);
                        setRangeTouched(true);
                      }}
                      value={dateRange}
                      locale={ptBR.DatePicker}
                      style={{width: 300}}
                      placeholder={["Data inicial", "Data final"]}
                      format={{ format: 'MM/YYYY', type: 'mask' }}
                      picker="month"
                    />
                  </Form.Item>
                </div>
                <Divider style={{margin: "16px 0"}} />
                <Row gutter={[16, 16]} className="summary-cards">
                  <Col xs={24} sm={12}>
                    <Card className="summary-card">
                      <div className="summary-card-content">
                        <CalendarOutlined className="summary-card-icon next-icon" />
                        <div>
                          <span className="summary-card-label">
                            Próximo pagamento{nextPayment?.date && <> em <span className="summary-card-date-highlight">{formatDate(nextPayment.date)}</span></>}
                          </span>
                          <span className="summary-card-value">
                            {nextPayment ? formatPrice(nextPayment.total / 100) : "—"}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card className="summary-card">
                      <div className="summary-card-content">
                        <DollarOutlined className="summary-card-icon total-icon" />
                        <div>
                          <span className="summary-card-label">Valor total a receber</span>
                          <span className="summary-card-value">
                            {total !== null ? formatPrice(total / 100) : "—"}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Card>

              <Card title="Recebimentos por mês" className="monthly-card" style={{marginBottom: 16}}>
                {!hasAnyMonthlyValue ? (
                  <Empty description="Não há dados disponíveis" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={monthlyChartData} margin={{top: 8, right: 16, left: 8, bottom: 0}}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                      <YAxis hide />
                      <Tooltip formatter={(value) => [formatPrice(value), "Total"]} />
                      <Bar dataKey="value" fill="#1677ff" radius={[4, 4, 0, 0]} />
                      <Customized component={renderTodayLine} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>

              <Card title="Calendário de recebimentos" className="calendar-card" style={{marginBottom: 16}}>
                <div className="calendar-container">
                  {months.map((month) => renderMonth(month))}
                </div>
              </Card>

              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Aviso"
                description="As informações acima representam apenas projeções estimadas, calculadas com base nos recebíveis das transações. Os valores estão sujeitos a alterações em função de movimentações na conta, especialmente chargebacks. Para vendedores com contas configuradas para saque automático diário, os valores exibidos em dias não úteis são acumulados e depositados em conjunto no primeiro dia útil subsequente. Utilize essas informações somente como referência e apoio à gestão financeira. Os valores finais estão sujeitos a ajustes pela adquirente antes da efetivação de qualquer pagamento."
              />
            </>
          )}
        </Content>
      </Layout>
    </div>
  );
};

export default ReceivableGraphic;
